import React, { useEffect, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Task, TaskStatus, TaskPriority } from "@/app/types/task";
import { Button } from "@/components/ui/button";
import { StickyNote } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { countNotesForEntity } from "@/app/services/noteService";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { TaskRowActions } from "./RowActions";

// Define the extended column type with meta
interface CustomColumnMeta {
  label?: string;
  placeholder?: string;
  variant?: 'text' | 'number' | 'select' | 'date' | 'dateRange' | 'boolean' | 'multiSelect';
  icon?: React.ComponentType<{ className?: string }>;
  options?: Array<{ label: string; value: string | number | boolean }>;
}

type ExtendedColumnDef<TData, TValue = unknown> = ColumnDef<TData, TValue> & {
  meta?: CustomColumnMeta;
};

// Helper component for the table header
export const DataTableColumnHeader = ({
  column,
  title,
}: {
  column: any;
  title: string;
}) => {
  return (
    <div className="flex items-center space-x-2">
      <span>{title}</span>
      {column.getCanSort() && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => column.toggleSorting()}
        >
          <span className="sr-only">Toggle sort</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="h-4 w-4"
          >
            <path d="m3 16 4 4 4-4" />
            <path d="M7 20V4" />
            <path d="m21 8-4-4-4 4" />
            <path d="M17 4v16" />
          </svg>
        </Button>
      )}
    </div>
  );
};

// Status badge colors
const getStatusVariant = (status: TaskStatus): "default" | "secondary" | "destructive" | "outline" => {
  switch (status) {
    case TaskStatus.COMPLETED:
      return "default"; // Green
    case TaskStatus.OPEN:
      return "secondary"; // Blue
    case TaskStatus.CANCELLED:
      return "destructive"; // Red
    default:
      return "outline";
  }
};

// Priority badge colors
const getPriorityVariant = (priority: TaskPriority): "default" | "secondary" | "destructive" | "outline" => {
  switch (priority) {
    case TaskPriority.HIGH:
      return "destructive"; // Red
    case TaskPriority.MEDIUM:
      return "default"; // Yellow/default
    case TaskPriority.LOW:
      return "secondary"; // Gray
    default:
      return "outline";
  }
};

// Notes indicator component
const NotesIndicator = ({ taskId }: { taskId: number }) => {
  const [notesCount, setNotesCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCount = async () => {
      try {
        const count = await countNotesForEntity('task', taskId);
        setNotesCount(count);
      } catch (error) {
        console.error('Failed to fetch notes count:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCount();
  }, [taskId]);

  if (loading || notesCount === 0) {
    return null;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-1 text-muted-foreground">
            <StickyNote className="h-4 w-4" />
            <span className="text-xs font-medium">{notesCount}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{notesCount} {notesCount === 1 ? 'note' : 'notes'}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Define columns for the Client Manager Tasks table
export const columns: ExtendedColumnDef<Task>[] = [
  {
    accessorKey: "title",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Title" />,
    cell: ({ row }) => {
      const task = row.original;
      return (
        <div className="max-w-md">
          <div className="flex items-center gap-2">
            <div className="font-medium truncate">{task.title}</div>
            <NotesIndicator taskId={task.id} />
          </div>
          {task.party?.name && (
            <div className="text-sm text-muted-foreground truncate">{task.party.name}</div>
          )}
          {task.description && (
            <div className="text-xs text-muted-foreground truncate">{task.description}</div>
          )}
        </div>
      );
    },
    enableColumnFilter: true,
    meta: {
      label: "Title",
      placeholder: "Filter by title...",
      variant: "text",
    },
  },
  {
    accessorKey: "status",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    cell: ({ row }) => {
      const status = row.getValue("status") as TaskStatus;
      return (
        <Badge variant={getStatusVariant(status)} className="capitalize">
          {status}
        </Badge>
      );
    },
    enableColumnFilter: true,
    meta: {
      label: "Status",
      placeholder: "Select status...",
      variant: "select",
      options: [
        { label: "Open", value: TaskStatus.OPEN },
        { label: "Completed", value: TaskStatus.COMPLETED },
        { label: "Cancelled", value: TaskStatus.CANCELLED },
      ],
    },
  },
  {
    accessorKey: "priority",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Priority" />,
    cell: ({ row }) => {
      const priority = row.getValue("priority") as TaskPriority;
      return (
        <Badge variant={getPriorityVariant(priority)} className="capitalize">
          {priority}
        </Badge>
      );
    },
    enableColumnFilter: true,
    meta: {
      label: "Priority",
      placeholder: "Select priority...",
      variant: "select",
      options: [
        { label: "High", value: TaskPriority.HIGH },
        { label: "Medium", value: TaskPriority.MEDIUM },
        { label: "Low", value: TaskPriority.LOW },
      ],
    },
  },
  {
    accessorKey: "task_type",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
    cell: ({ row }) => {
      const taskType = row.getValue("task_type") as string | null;
      if (!taskType) return <div className="text-muted-foreground">-</div>;
      const label = taskType.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      return (
        <Badge variant="outline" className="capitalize">
          {label}
        </Badge>
      );
    },
    enableColumnFilter: false,
  },
  {
    accessorKey: "assigned_to_user_id",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Assigned To" />,
    cell: ({ row }) => {
      const task = row.original;
      return (
        <div className="text-sm">
          {task.assigned_to?.full_name || task.assigned_to?.username || "-"}
        </div>
      );
    },
    enableColumnFilter: true,
    meta: {
      label: "Assigned To",
      placeholder: "Select user...",
      variant: "select",
      options: [], // Will be populated dynamically
    },
  },
  {
    accessorKey: "team_id",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Team" />,
    cell: ({ row }) => {
      const task = row.original;
      if (!task.team) return <div className="text-muted-foreground text-sm">-</div>;
      const isPool = !task.assigned_to_user_id;
      return (
        <Badge variant={isPool ? "secondary" : "outline"} className="font-normal">
          {task.team.name}{isPool ? " · pool" : ""}
        </Badge>
      );
    },
    enableColumnFilter: true,
    meta: {
      label: "Team",
      placeholder: "Select team...",
      variant: "select",
      options: [], // Populated dynamically
    },
  },
  {
    accessorKey: "due_date",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Due Date" />,
    cell: ({ row }) => {
      const dueDate = row.getValue("due_date") as string | null;
      if (!dueDate) return <div className="text-muted-foreground">-</div>;

      const date = new Date(dueDate);
      const isOverdue = date < new Date() && row.original.status === TaskStatus.OPEN;

      return (
        <div className={isOverdue ? "text-destructive font-medium" : ""}>
          {format(date, "MMM d, yyyy")}
        </div>
      );
    },
  },
  {
    accessorKey: "created_at",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Created" />,
    cell: ({ row }) => {
      const createdAt = row.getValue("created_at") as string;
      const task = row.original;
      return (
        <div className="text-sm">
          <div>{format(new Date(createdAt), "MMM d, yyyy")}</div>
          {task.creator && (
            <div className="text-xs text-muted-foreground">
              by {task.creator.full_name || task.creator.username}
            </div>
          )}
        </div>
      );
    },
  },
  {
    id: "actions",
    cell: ({ row }) => <TaskRowActions task={row.original} />,
  },
];
